/**
 * Zone-aware date primitives — the single source of calendar date arithmetic for this surface.
 *
 * Every function takes the tenant's IANA zone identifier as an explicit parameter. None reads a
 * global, a React context or a default, and none falls back to the browser's zone when the zone
 * argument is missing: an absent zone is a caller error, and swallowing it re-creates the very
 * defect this module exists to remove — a calendar that answers in whichever zone the operator's
 * laptop happens to be set to.
 *
 * A browser-local date construction anywhere under `src/pages/vektra/calendar/` is a defect. The
 * local-field `Date` accessors and the locale date/time formatters called without an explicit zone
 * all read the host clock, while the availability API buckets every day boundary in the tenant's
 * operational zone (`operational_schedules.time_zone`). Mixing the two puts a third clock on screen.
 *
 * Phase 3's own research names **duplication, rather than missing capability**, as the cause of
 * every defect it fixes: the same day-boundary logic was written out again in four files and the
 * copies drifted apart. A helper added here without deleting the copies it replaces has not fixed
 * that class of defect.
 *
 * Mechanism: `Intl.DateTimeFormat` with an explicit `timeZone`, per the phase's Timezone Rendering
 * Contract. It is the platform API and needs no dependency — this module adds no package.
 */

/** A calendar date as it reads on a wall in some zone. `month` is 1-12 and `day` is 1-31. */
export interface ZonedDateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * A fixed, neutral locale for the parts formatter. It pins the Gregorian calendar and plain numeric
 * fields regardless of the user's locale, and orders them year-month-day so they join straight into
 * the `YYYY-MM-DD` string the availability API's `date` parameter takes.
 */
const PARTS_LOCALE = 'en-CA';

const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

/** `Intl.DateTimeFormat` construction is the expensive half of every call below, so keep one per zone. */
const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(PARTS_LOCALE, {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    partsFormatters.set(timeZone, formatter);
  }
  return formatter;
}

interface WallClock extends ZonedDateParts {
  hour: number;
  minute: number;
  second: number;
}

/** The wall-clock reading in `timeZone` at the given instant. */
function wallClockAt(instantMs: number, timeZone: string): WallClock {
  const parts = partsFormatter(timeZone).formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second')
  };
}

/** The zone's UTC offset in milliseconds at a given instant, derived from its own wall clock. */
function offsetMsAt(instantMs: number, timeZone: string): number {
  const wall = wallClockAt(instantMs, timeZone);
  const asIfUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  // The formatter has second resolution, so compare against the instant truncated to seconds.
  return asIfUtc - Math.floor(instantMs / 1000) * 1000;
}

/**
 * The instant at which a wall-clock calendar date begins in `timeZone`.
 *
 * The target semantics, stated so the claim below is checkable rather than trusted: **the earliest
 * instant whose wall-clock date in the zone equals the requested date.** That is what Java's
 * `LocalDate.atStartOfDay(ZoneId)` returns, for a gap and for an overlap alike, and it is therefore
 * the parity this module needs in order for the client and the server to bucket the same day the
 * same way — which is CAL-03's whole subject.
 *
 * Two passes, then one test. The offset in force at the naive guess can differ from the offset in
 * force at the answer across a DST transition, and the second pass settles on the correct one for
 * every ordinary day and for the common 02:00 and 03:00 transitions. Where the requested midnight
 * **does not exist** — a handful of zones spring forward at exactly 00:00 — the second pass subtracts
 * the post-transition offset from a wall-clock midnight that never occurred and lands at 23:00 on the
 * *previous* calendar day. The first pass has already landed on the instant the clock jumps to, so
 * when the second pass's answer does not read as the requested date, the first pass's answer is the
 * one to keep.
 *
 * Advancing a day and re-resolving the offset — the obvious repair, and the one sketched in this
 * phase's client review — does **not** work: it returns the same wrong instant. Verified against
 * `America/Santiago` 2026-09-06.
 *
 * `scripts/checks/zone-arithmetic.test.mjs` checks all of this against an independently computed
 * oracle, on every offset-transition day in nine zones across four years.
 */
function instantAtStartOfDay(date: ZonedDateParts, timeZone: string): Date {
  const wallMs = Date.UTC(date.year, date.month - 1, date.day);
  const firstPassMs = wallMs - offsetMsAt(wallMs, timeZone);
  const secondPassMs = wallMs - offsetMsAt(firstPassMs, timeZone);

  const wall = wallClockAt(secondPassMs, timeZone);
  const readsAsRequestedDate = wall.year === date.year && wall.month === date.month && wall.day === date.day;

  return new Date(readsAsRequestedDate ? secondPassMs : firstPassMs);
}

/**
 * Calendar-day arithmetic with no zone in it: `Date.UTC` is used purely as a Gregorian calendar,
 * never as a clock, so out-of-range days and months normalise the way the calendar does.
 */
function shiftDays(date: ZonedDateParts, delta: number): ZonedDateParts {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + delta));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

function parseIsoDate(iso: string): ZonedDateParts {
  const [year, month, day] = iso.split('-').map(Number);
  return { year, month, day };
}

function toParts(date: Date | string, timeZone: string): ZonedDateParts {
  return typeof date === 'string' ? parseIsoDate(date) : partsInZone(date, timeZone);
}

/**
 * A `YYYY-MM-DD` calendar date built from its parts, with no zone in it: `Date.UTC` is used purely
 * as a Gregorian calendar and never as a clock, so an overflowing day or month normalises the way
 * the calendar does — day 32 of January is 1 February. Feed the result to `startOfDayInZone` to turn
 * it into an instant. It lives here so the page and the toolbar share one copy of the arithmetic.
 */
export function calendarDate(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

/**
 * Whether this browser can interpret `timeZone` at all. The tenant's zone arrives from the server as
 * an opaque string; handing an unrecognised one to `Intl` throws a `RangeError` mid-render, so a
 * caller that cannot show an error for a bad zone must check first. This reports usability — it
 * never substitutes a working zone for a broken one.
 */
export function isUsableZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone || !timeZone.trim()) return false;
  try {
    return new Intl.DateTimeFormat(PARTS_LOCALE, { timeZone }).resolvedOptions().timeZone !== undefined;
  } catch {
    return false;
  }
}

/**
 * Format an instant as it reads in `timeZone`. Defaults to a 24-hour time; pass `options` for a date.
 * The locale is the user's, the zone never is.
 */
export function formatInZone(instant: Date, timeZone: string, options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS): string {
  return new Intl.DateTimeFormat([], { ...options, timeZone }).format(instant);
}

/** The calendar-date parts of an instant as they read in `timeZone`. */
export function partsInZone(instant: Date, timeZone: string): ZonedDateParts {
  const wall = wallClockAt(instant.getTime(), timeZone);
  return { year: wall.year, month: wall.month, day: wall.day };
}

/** The `YYYY-MM-DD` the availability API's `date` parameter takes, for the day this instant falls on in `timeZone`. */
export function isoDateInZone(instant: Date, timeZone: string): string {
  const { year, month, day } = partsInZone(instant, timeZone);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The instant at which a calendar day begins in `timeZone` — either the day a given instant falls on,
 * or a `YYYY-MM-DD` date named directly.
 */
export function startOfDayInZone(date: Date | string, timeZone: string): Date {
  return instantAtStartOfDay(toParts(date, timeZone), timeZone);
}

/**
 * The wall-clock hour of an instant as it reads in `timeZone`, as a fractional number — hours plus
 * minutes over sixty, with seconds folded in at the same scale.
 *
 * This is the axis the day view labels its hour columns with, so it is the axis blocks have to be
 * positioned on. Measuring elapsed time from the day's start instead disagrees with that axis by the
 * size of the shift for every instant after a transition, which is how a 09:00 rental came to be
 * drawn in the 08:00 column with a tooltip that read "09:00".
 */
export function wallHoursInZone(instant: Date, timeZone: string): number {
  const wall = wallClockAt(instant.getTime(), timeZone);
  return wall.hour + wall.minute / 60 + wall.second / 3600;
}

/**
 * The whole-day difference `a - b` between two `YYYY-MM-DD` dates, as pure Gregorian arithmetic.
 *
 * No zone and no clock: `Date.UTC` is used here only as a calendar, the same way `shiftDays` uses it.
 * Two calendar dates are a fixed number of days apart whatever the offsets in between did, so a
 * transition day counts as one day — which is exactly what a grid drawn in wall-clock hours needs.
 */
export function isoDayDifference(a: string, b: string): number {
  const left = parseIsoDate(a);
  const right = parseIsoDate(b);
  const leftMs = Date.UTC(left.year, left.month - 1, left.day);
  const rightMs = Date.UTC(right.year, right.month - 1, right.day);
  return Math.round((leftMs - rightMs) / 86_400_000);
}

/**
 * The instant at which the week containing `date` begins in `timeZone`. Monday, preserving the ISO
 * week-start convention the toolbar has always used.
 */
export function startOfWeekInZone(date: Date | string, timeZone: string): Date {
  const parts = toParts(date, timeZone);
  // Day-of-week off the pure Gregorian calendar, so the answer does not depend on the host clock.
  const dayOfWeek = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay(); // 0=Sun..6=Sat
  return instantAtStartOfDay(shiftDays(parts, -((dayOfWeek + 6) % 7)), timeZone);
}
